import React, { useState, useEffect, useRef } from 'react';
import PageTemplate from '../components/PageTemplate';
import lineupVideo from '../lineupVideo.mp4';

export default function Home() {
  const videoRef = useRef(null);
  const playAttemptedRef = useRef(false);
  const showHeroTimeoutRef = useRef(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showHeroContent, setShowHeroContent] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const attemptAutoplay = async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.play();
    } catch (error) {
      // Autoplay can fail if browser blocks it; we'll retry on visibility
    }
  };

  // Force video reset on mount to fix decode/hydration timing issue
  useEffect(() => {
    setVideoEnded(false);
    playAttemptedRef.current = false;
    setShowHeroContent(false);
    if (showHeroTimeoutRef.current) {
      clearTimeout(showHeroTimeoutRef.current);
      showHeroTimeoutRef.current = null;
    }
    
    // Force complete video reset: pause, reset time, reload
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      videoRef.current.load();
    }

    setIsMuted(true);
    requestAnimationFrame(() => {
      attemptAutoplay();
    });
    const retryTimer = setTimeout(() => {
      attemptAutoplay();
    }, 350);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        attemptAutoplay();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return (
    <PageTemplate>
      {/* Hero Section */}
      <div 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          onCanPlayThrough={async () => {
            // Wait for canplaythrough - video decode pipeline is fully ready
            if (videoRef.current && !playAttemptedRef.current && !videoEnded) {
              playAttemptedRef.current = true;
              try {
                // Ensure video is at start and muted for autoplay
                videoRef.current.currentTime = 0;
                videoRef.current.muted = true;
                
                // Play only after canplaythrough confirms readiness
                await videoRef.current.play();
              } catch (error) {
                // If autoplay fails, reset attempt flag
                playAttemptedRef.current = false;
              }
            }
          }}
          onPlay={() => {}}
          onError={(e) => {
            console.error('Video error:', e);
            playAttemptedRef.current = false;
            showHeroTimeoutRef.current = setTimeout(() => {
              setShowHeroContent(true);
            }, 200);
          }}
          onTimeUpdate={() => {
            if (videoRef.current) {
              const video = videoRef.current;
              const duration = video.duration;
              const currentTime = video.currentTime;
              
              // Fade out audio in the last 3 seconds
              if (duration && currentTime > duration - 3) {
                const fadeOutDuration = 3; // seconds
                const fadeOutStart = duration - fadeOutDuration;
                const volume = Math.max(0, 1 - (currentTime - fadeOutStart) / fadeOutDuration);
                video.volume = volume;
              } else if (video.volume !== 1) {
                video.volume = 1;
              }
            }
          }}
          onEnded={() => {
            if (videoRef.current) {
              videoRef.current.volume = 0;
            }
            setVideoEnded(true);
            showHeroTimeoutRef.current = setTimeout(() => {
              setShowHeroContent(true);
            }, 150);
          }}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoEnded ? 'opacity-0' : 'opacity-100'
          }`}
          poster="/LandingImage.png"
        >
          <source src={lineupVideo} type="video/mp4" />
        </video>
        
        {/* Background image that appears after video ends */}
        <div 
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
            videoEnded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: "url('/LandingImage.png')"
          }}
        />
        
        {/* Dimmed Overlay for better text readability */}
        <div className={`absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50 transition-opacity duration-1000 ${
          showHeroContent ? 'opacity-70' : 'opacity-0'
        }`} />
        
        {/* Additional subtle gradient overlay for aesthetic */}
        <div className={`absolute inset-0 bg-gradient-to-b from-beach-900/30 via-beach-800/20 to-sand-900/30 transition-opacity duration-1000 ${
          showHeroContent ? 'opacity-70' : 'opacity-0'
        }`} />
        
        {/* Content */}
        <div className={`relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto transition-opacity duration-1000 ${
          showHeroContent ? 'opacity-100' : 'opacity-0'
        }`}>
          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[12rem] mb-8 sm:mb-12 leading-tight drop-shadow-2xl lobster-regular" 
              style={{ color: "#FFE15D", textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}>
            NJ Naach
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white drop-shadow-lg mt-2 lobster-regular">3.0</span>
          </h1>
          
        </div>

        {/* Mute Toggle */}
        <button
          type="button"
          onClick={() => {
            if (!videoRef.current) return;
            const nextMuted = !isMuted;
            videoRef.current.muted = nextMuted;
            if (!nextMuted) {
              videoRef.current.volume = 1;
            }
            setIsMuted(nextMuted);
          }}
          className="absolute top-4 right-4 z-20 rounded-full bg-black/40 text-white text-xs sm:text-sm px-3 py-2 backdrop-blur-sm hover:bg-black/60 transition"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? "Unmute" : "Mute"}
        </button>
      </div>

      {/* Coming Soon Section */}
      <div className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 bg-gradient-to-b from-cream-50 to-peach-50 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-text font-bold text-coral mb-6 sm:mb-8 animate-fade-in">
            Stay tuned
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl font-text text-teal-600 mb-10 sm:mb-12 animate-fade-in">
            NJ Naach 4.0 updates are coming soon.
          </p>
          <div className="mx-auto max-w-2xl bg-white/80 backdrop-blur-md rounded-3xl border-2 border-peach-200 shadow-xl p-6 sm:p-8 animate-slide-up">
            <p className="text-base sm:text-lg md:text-xl font-text text-coral">
              Follow along for announcements on dates, lineup, and tickets.
            </p>
          </div>
        </div>
      </div>
    </PageTemplate>
  );
}
